import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import style from "./style";
import DragAndDropMultiple from "../../../../components/DragAndDropMultiple";
import { useNavigate } from "react-router-dom";
import { LoadingButton } from "@mui/lab";
import { Button, Checkbox, FormControl, FormControlLabel, FormLabel, MenuItem, Radio, RadioGroup, Select, TextField } from "@mui/material";
import { CreateInventoryValidation } from "../../../../helper/Validation";

const EditItem = ({ handleEdit, loading, supplierList, ItemData }) => {
  const [files, setFiles] = useState();
  const [formData, setFormData] = useState({
    type: "Goods",
    title: "",
    unit: "",
    SKU: "",
    category: "",
    salesInformation: {
      sellingPrice: "",
      account: "",
      description: ""
    },
    purchaseInformation: {
      costPrice: "",
      account: "",
      description: "",
      preferredSupplier: ""
    },
    quantity: ""
  });
  const [errors, setErrors] = useState({
    type: "",
    title: "",
    unit: "",
    SKU: "",
    category: "",
    salesInformation: {
      sellingPrice: "",
      account: "",
      description: "",
    },
    purchaseInformation: {
      costPrice: "",
      account: "",
      description: "",
      preferredSupplier: "",
    },
    quantity: ""
  });
  const [trackInventory, setTrackInventory] = useState(true);
  const [showSalesInfo, setShowSalesInfo] = useState(false);
  const [showPurchaseInfo, setShowPurchaseInfo] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (ItemData) {
      setFormData({
        type: ItemData.type || "Goods",
        title: ItemData.title || "",
        unit: ItemData.unit || "",
        SKU: ItemData.SKU || "",
        category: ItemData.category || "",
        salesInformation: {
          sellingPrice: ItemData.salesInformation?.sellingPrice || "",
          account: ItemData.salesInformation?.account || "",
          description: ItemData.salesInformation?.description || ""
        },
        purchaseInformation: {
          costPrice: ItemData.purchaseInformation?.costPrice || "",
          account: ItemData.purchaseInformation?.account || "",
          description: ItemData.purchaseInformation?.description || "",
          preferredSupplier: ItemData.purchaseInformation?.preferredSupplier || ""
        },
        quantity: ItemData.quantity || ""
      });
      setTrackInventory(ItemData.quantity ? true : false);
    }
  }, [ItemData]);

  const handleNavigateBack = () => {
    navigate("/dashboard/items");
  };

  const validateField = (fieldName, value) => {
    const rule = CreateInventoryValidation[fieldName];

    if (!rule) return ""; // No rule defined, consider valid

    // Check if required field is empty
    if (rule.required && !value) {
      return rule.validator.errEmpty;
    }

    // Skip validation for empty non-required fields
    if (!rule.required && !value) {
      return "";
    }

    // Validate against regex pattern
    if (!rule.validator.regEx.test(value)) {
      return rule.validator.error;
    }

    return ""; // No error
  };

  const handleFormChange = (e) => {
    if (!e || !e.target) return;

    const { name, value } = e.target;
    if (!name) return;

    // Validate the field
    const error = validateField(name, value);

    setErrors(prev => ({
      ...prev,
      [name]: error
    }));

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');

      // Validate the field
      const error = validateField(child, value);

      setErrors(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: error
        }
      }));

      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      // Validate the field
      const error = validateField(field, value);

      setErrors(prev => ({
        ...prev,
        [field]: error
      }));

      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const normalizedQuantity = Number(formData.quantity || 0);
    const payload = {
      type: formData.type,
      title: formData.title,
      unit: formData.unit,
      SKU: formData.SKU,
      category: formData.category,
      salesInformation: {
        sellingPrice: Number(formData.salesInformation.sellingPrice),
        account: formData.salesInformation.account,
        description: formData.salesInformation.description
      },
      purchaseInformation: {
        costPrice: Number(formData.purchaseInformation.costPrice),
        account: formData.purchaseInformation.account,
        description: formData.purchaseInformation.description,
        preferredSupplier: formData.purchaseInformation.preferredSupplier
      },
      quantity: normalizedQuantity
    };
    if (formData.type === "Goods") {
      payload.quantity = trackInventory ? normalizedQuantity : 0;
    }
    handleEdit(payload, files);
  };

  const textFieldStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      '&.Mui-error fieldset': {
        borderColor: 'red',
      },
    },
  };

  // Check if form is valid for submit button
  const isFormValid = () => {
    const baseValid = formData.title && formData.unit && formData.category && formData.SKU &&
      !errors.title && !errors.unit && !errors.category && !errors.SKU;

    const salesValid = !showSalesInfo || (
      formData.salesInformation.sellingPrice &&
      !errors.salesInformation?.sellingPrice
    );

    const purchaseValid = !showPurchaseInfo || (
      formData.purchaseInformation.costPrice &&
      !errors.purchaseInformation?.costPrice
    );

    return baseValid && salesValid && purchaseValid;
  };
  return (
    <>
      <Box px="15px" mt={2}>
        <Box sx={style.headerContainer}>
          <Typography sx={style.leaveHeading}>Edit Inventory</Typography>
        </Box>
      </Box>
      <form onSubmit={handleSubmit}>
        <Grid container>
          <Grid item xs={12} lg={7.5} sx={style.gridbox}>
            <Box sx={style.inputSection}>
              <Box sx={style.inputBox}>
                <FormControl>
                  <FormLabel sx={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#333',
                    marginBottom: '8px',
                    marginTop: '10px'
                  }}>
                    Type
                  </FormLabel>
                  <RadioGroup
                    row
                    name="type"
                    value={formData.type}
                    onChange={handleFormChange}
                  >
                    <FormControlLabel
                      value="Goods"
                      control={<Radio size="small" />}
                      label={<Typography fontSize="14px">Goods</Typography>}
                    />
                    <FormControlLabel
                      value="Service"
                      control={<Radio size="small" />}
                      label={<Typography fontSize="14px">Services</Typography>}
                    />
                  </RadioGroup>
                </FormControl>
              </Box>
              <Box sx={style.inputBox}>
                <Typography sx={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#333',
                  marginBottom: '8px',
                  marginTop: '10px'
                }}>
                  Item Title
                </Typography>
                <TextField
                  variant="outlined"
                  required
                  fullWidth
                  placeholder="Enter item title"
                  name="title"
                  value={formData.title}
                  onChange={handleFormChange}
                  autoComplete="title"
                  error={!!errors.title}
                  helperText={errors.title}
                  sx={{
                    ...textFieldStyles,
                    '& .MuiOutlinedInput-root': {
                      fontSize: '14px',
                      borderRadius: '8px'
                    }
                  }}
                />
              </Box>
            </Box>
            <Box sx={style.inputSection}>
              <Box sx={style.inputBox}>
                <FormControl fullWidth sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "2.8em",
                    borderRadius: "8px",
                    fontSize: '14px'
                  }
                }}>
                  <Typography sx={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#333',
                    marginBottom: '8px',
                    marginTop: '10px'
                  }}>
                    Unit
                  </Typography>
                  <Select
                    fullWidth
                    required
                    id="unit"
                    name="unit"
                    value={formData.unit}
                    onChange={handleFormChange}
                    displayEmpty
                    error={!!errors.unit}
                    sx={{
                      ...style.dropDownMenu,
                      ...textFieldStyles,
                      fontSize: '14px'
                    }}
                  >
                    <MenuItem value="" disabled>Select Unit</MenuItem>
                    <MenuItem value="box">Box</MenuItem>
                    <MenuItem value="cm">cm</MenuItem>
                    <MenuItem value="dz">dz</MenuItem>
                    <MenuItem value="ft">ft</MenuItem>
                    <MenuItem value="g">g</MenuItem>
                    <MenuItem value="in">in</MenuItem>
                    <MenuItem value="kg">kg</MenuItem>
                    <MenuItem value="km">km</MenuItem>
                    <MenuItem value="lb">lb</MenuItem>
                    <MenuItem value="mg">mg</MenuItem>
                    <MenuItem value="ml">ml</MenuItem>
                    <MenuItem value="m">m</MenuItem>
                    <MenuItem value="pcs">pcs</MenuItem>
                  </Select>
                </FormControl>
                {errors.unit && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                    {errors.unit}
                  </Typography>
                )}
              </Box>
              <Box sx={style.inputBox}>
                <FormControl fullWidth sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "2.8em",
                    borderRadius: "8px",
                    fontSize: '14px'
                  }
                }}>
                  <Typography sx={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#333',
                    marginBottom: '8px',
                    marginTop: '10px'
                  }}>
                    Category
                  </Typography>
                  <Select
                    fullWidth
                    required
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleFormChange}
                    displayEmpty
                    error={!!errors.category}
                    sx={{
                      ...style.dropDownMenu,
                      fontSize: '14px'
                    }}
                  >
                    <MenuItem value="" disabled>Select Category</MenuItem>
                    <MenuItem value="Electronics">Electronics</MenuItem>
                    <MenuItem value="Clothing">Clothing</MenuItem>
                    <MenuItem value="Food">Food</MenuItem>
                  </Select>
                  {errors.category && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                      {errors.category}
                    </Typography>
                  )}
                </FormControl>
              </Box>
            </Box>
            <Box sx={style.inputSection}>
              <Box sx={style.inputBox}>
                <Typography sx={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#333',
                  marginBottom: '8px',
                  marginTop: '10px'
                }}>
                  SKU
                </Typography>
                <TextField
                  variant="outlined"
                  required
                  fullWidth
                  id="SKU"
                  placeholder="Enter SKU"
                  name="SKU"
                  value={formData.SKU}
                  onChange={handleFormChange}
                  error={!!errors.SKU}
                  helperText={errors.SKU}
                  autoComplete="SKU"
                  sx={{
                    ...textFieldStyles,
                    '& .MuiOutlinedInput-root': {
                      fontSize: '14px',
                      borderRadius: '8px'
                    }
                  }}
                />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} lg={4} sx={style.gridbox}>
            <Box sx={{ ...style.productSection, minHeight: '224px' }}>
              <Typography sx={{
                fontSize: '14px',
                fontWeight: '500',
                color: '#333',
                marginBottom: '8px',
                marginTop: '10px'
              }}>
                Product Images
              </Typography>
              <DragAndDropMultiple handleUrl={(file) => setFiles(file)} />
            </Box>
          </Grid>
        </Grid>
        <Box sx={style.gridbox}>
          <Grid container>
            <Grid item xs={12} lg={6} >
              <Box sx={style.leaveSection}>
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showSalesInfo}
                        onChange={(e) => setShowSalesInfo(e.target.checked)}
                        size="small"
                      />
                    }
                    label="Sales Information"
                  />
                </Box>
              </Box>
              <Box sx={style.leaveSection}>
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'row', gap: "40px" }}>
                  <Typography sx={style.inputTextNew}>Selling Price<span style={{ color: "red" }}>*</span> </Typography>
                  <TextField
                    variant="outlined"
                    required
                    fullWidth
                    type="number"
                    placeholder="Selling Price"
                    name="sellingPrice"
                    value={formData.salesInformation.sellingPrice}
                    onChange={(e) => handleChange("salesInformation.sellingPrice", e.target.value)}
                    autoComplete="sellingPrice"
                    error={!!errors.salesInformation?.sellingPrice}
                    helperText={errors.salesInformation?.sellingPrice}
                    sx={{
                      ...textFieldStyles,
                      '& .MuiOutlinedInput-root': {
                        fontSize: '14px',
                        borderRadius: '8px'
                      }
                    }}
                  />
                </Box>
              </Box>
              <Box sx={style.leaveSection}>
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'row', gap: "40px" }}>
                  <Typography sx={style.inputText}>Account<span style={{ color: "red" }}>*</span></Typography>
                  <TextField
                    variant="outlined"
                    fullWidth
                    placeholder="Account"
                    name="salesAccount"
                    value={formData.salesInformation.account}
                    onChange={(e) => handleChange("salesInformation.account", e.target.value)}
                    autoComplete="salesAccount"
                    error={!!errors.salesInformation?.account}
                    helperText={errors.salesInformation?.account}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '14px',
                        borderRadius: '8px'
                      }
                    }}
                  />
                </Box>
              </Box>
              <Box sx={style.leaveSection}>
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'row', gap: "40px" }}>
                  <Typography sx={style.inputText}>Description</Typography>
                  <TextField
                    variant="outlined"
                    fullWidth
                    placeholder="Description"
                    name="salesDescription"
                    value={formData.salesInformation.description}
                    onChange={(e) => handleChange("salesInformation.description", e.target.value)}
                    autoComplete="salesDescription"
                    error={!!errors.salesInformation?.description}
                    helperText={errors.salesInformation?.description}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '14px',
                        borderRadius: '8px'
                      }
                    }}
                  />
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} lg={6} >
              <Box sx={style.leaveSection}>
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showSalesInfo}
                        onChange={(e) => setShowSalesInfo(e.target.checked)}
                        size="small"
                      />
                    }
                    label="Purchase Information"
                  />
                </Box>
              </Box>
              <Box sx={style.leaveSection}>
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'row', gap: "40px" }}>
                  <Typography sx={style.inputText}>Cost Price<span style={{ color: "red" }}>*</span> </Typography>
                  <TextField
                    variant="outlined"
                    required
                    type="number"
                    fullWidth
                    placeholder="Cost Price"
                    name="costPrice"
                    value={formData.purchaseInformation.costPrice}
                    onChange={(e) => handleChange("purchaseInformation.costPrice", e.target.value)}
                    autoComplete="costPrice"
                    error={!!errors.purchaseInformation?.costPrice}
                    helperText={errors.purchaseInformation?.costPrice}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '14px',
                        borderRadius: '8px'
                      }
                    }}
                  />
                </Box>

              </Box>
              <Box sx={style.leaveSection}>
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'row', gap: "40px" }}>
                  <Typography sx={style.inputText}>Account</Typography>
                  <TextField
                    variant="outlined"
                    fullWidth
                    placeholder="Account"
                    name="purchaseAccount"
                    value={formData.purchaseInformation.account}
                    onChange={(e) => handleChange("purchaseInformation.account", e.target.value)}
                    autoComplete="purchaseAccount"
                    error={!!errors.purchaseInformation?.account}
                    helperText={errors.purchaseInformation?.account}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '14px',
                        borderRadius: '8px'
                      }
                    }}
                  />
                </Box>
              </Box>
              <Box sx={style.leaveSection}>
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'row', gap: "40px" }}>
                  <Typography sx={style.inputText}>Description</Typography>
                  <TextField
                    variant="outlined"
                    fullWidth
                    placeholder="Description"
                    name="purchaseDescription"
                    value={formData.purchaseInformation.description}
                    onChange={(e) => handleChange("purchaseInformation.description", e.target.value)}
                    autoComplete="purchaseDescription"
                    error={!!errors.purchaseInformation?.description}
                    helperText={errors.purchaseInformation?.description}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '14px',
                        borderRadius: '8px'
                      }
                    }}
                  />
                </Box>
              </Box>
              <Box sx={style.leaveSection}>
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'row', gap: "40px" }}>
                  <Typography sx={style.inputText}>Preferred Supplier</Typography>
                  <Select
                    fullWidth
                    name="preferredSupplier"
                    value={formData.purchaseInformation.preferredSupplier}
                    onChange={(e) => handleChange("purchaseInformation.preferredSupplier", e.target.value)}
                    displayEmpty
                    error={!!errors.purchaseInformation?.preferredSupplier}
                    sx={{
                      ...style.dropDownMenu,
                      '& .MuiOutlinedInput-root': {
                        fontSize: '14px',
                        borderRadius: '8px'
                      }
                    }}
                  >
                    <MenuItem value="" disabled>Select Supplier</MenuItem>
                    {supplierList?.map((supplier) => (
                      <MenuItem key={supplier._id} value={supplier._id}>
                        {supplier.name}
                      </MenuItem>
                    ))}
                    {errors.purchaseInformation?.preferredSupplier && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                        {errors.purchaseInformation.preferredSupplier}
                      </Typography>
                    )}
                  </Select>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
        {formData.type === "Goods" && (
          <Box sx={style.gridbox}>
            <Grid container>
              <Grid item xs={12} lg={6}>
                <Box sx={style.leaveSection}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={trackInventory}
                        onChange={(e) => setTrackInventory(e.target.checked)}
                        size="small"
                        sx={{ color: '#2a2e2bff' }}
                      />
                    }
                    label={
                      <Box>
                        <Typography fontSize="14px" fontWeight={500}>
                          Track Inventory for this Item
                        </Typography>
                        <Typography fontSize="12px" color="#878787" sx={{ mt: 0.5 }}>
                          {"You cannot enable/disable inventory tracking once you've created transactions for this item"}
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              </Grid>
              <Grid item xs={12} lg={6} >
                <Box sx={{ width: '90%', display: 'flex', flexDirection: 'column', gap: "10px" }}>
                  <Typography sx={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#333'
                  }}>
                    Opening Stock
                  </Typography>
                  <TextField
                    variant="outlined"
                    fullWidth
                    type="number"
                    placeholder="0"
                    name="quantity"
                    value={formData.quantity}
                    onChange={(e) => handleChange("quantity", e.target.value)}
                    autoComplete="quantity"
                    error={!!errors.quantity}
                    helperText={errors.quantity}
                    sx={{
                      ...textFieldStyles,
                      '& .MuiOutlinedInput-root': {
                        fontSize: '14px',
                        borderRadius: '8px'
                      }
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}

        <Box sx={style.mainBoxes}>
          <Box sx={style.createJob}>
            <LoadingButton
              loading={loading}
              variant="contained"
              type="submit"
              disabled={!isFormValid()}
              color="success"
              sx={style.jobButtons}
            >
              Save
            </LoadingButton>
          </Box>
          <Box sx={style.createJob}>
            <Button variant="contained" sx={style.cancelButtons} onClick={handleNavigateBack}>
              Cancel
            </Button>
          </Box>
        </Box>
      </form >
    </>
  );
};

export default EditItem;
